/**
 * Prerendering utilities for react-snap compatibility
 */
import React from 'react';

/**
 * Detects if the current environment is prerendering (react-snap)
 * @returns {boolean} True if currently prerendering
 */
export const isPrerendering = () => {
  // Check for react-snap user agent
  if (typeof window !== 'undefined' && window.navigator) {
    return window.navigator.userAgent === 'ReactSnap';
  }
  
  // Server-side rendering detection
  return typeof window === 'undefined';
};

/**
 * Detects if the current environment is server-side rendering
 * @returns {boolean} True if server-side rendering
 */
export const isSSR = () => {
  return typeof window === 'undefined';
};

/**
 * Safely gets a value that may not exist during prerendering
 * @param {Function} getter - Function that returns the value
 * @param {*} fallback - Fallback value if getter fails
 * @returns {*} The value or fallback
 */
export const safeGet = (getter, fallback = null) => {
  if (isPrerendering()) {
    return fallback;
  }
  
  try {
    return getter();
  } catch (error) {
    return fallback;
  }
};

/**
 * Conditionally renders content based on prerendering state
 * @param {React.Component} prerenderComponent - Component to show during prerendering
 * @param {React.Component} clientComponent - Component to show after hydration
 * @returns {React.Component} The appropriate component
 */
export const ConditionalRender = ({ prerenderComponent, clientComponent }) => {
  if (isPrerendering()) {
    return prerenderComponent;
  }
  return clientComponent;
};

/**
 * Hook to detect hydration state
 * @returns {boolean} True if component has hydrated
 */
export const useHydrated = () => {
  const [hydrated, setHydrated] = React.useState(false);
  
  React.useEffect(() => {
    setHydrated(true);
  }, []);
  
  return hydrated;
};

/**
 * Gets model data from cache during prerendering
 * @param {string} modelName - Name of the model
 * @returns {Object|null} Cached model data or null
 */
export const getCachedModelData = (modelName) => {
  if (!isPrerendering()) {
    return null;
  }
  
  try {
    // Try to import cached model data
    const modelCache = require('../cache/model-data.json');
    return modelCache[modelName] || null;
  } catch (error) {
    console.warn('Could not load model cache:', error.message);
    return null;
  }
};

/**
 * Default model data for prerendering when cache is unavailable
 */
export const getDefaultModelData = (modelName) => ({
  name: modelName,
  description: `Custom ${modelName.replace(/-/g, ' ')} woodworking plans with detailed instructions and cut lists.`,
  amount: '$9.99',
  images: {
    sample: []
  },
  meta: {
    dimensions: {
      length: 0,
      width: 0,
      height: 0
    },
    weight: 0,
    number_of_parts: 0,
    unique_parts: 0
  }
});

/**
 * Gets model data for prerendering (cached or default)
 * @param {string} modelName - Name of the model
 * @returns {Object} Model data for prerendering
 */
export const getPrerenderModelData = (modelName) => {
  const cachedData = getCachedModelData(modelName);
  if (cachedData) {
    return cachedData;
  }
  
  return getDefaultModelData(modelName);
};